import { fitImageRect } from '../utils/eventImageFraming';

// Web replacement for TonyteCropModal.tsx's single native-only call:
// `captureRef(viewRef, { format: 'jpg', quality: 0.92, result: 'tmpfile' })`
// from react-native-view-shot (whose web stub, src/web-stubs/viewShot.ts,
// throws — there is no DOM equivalent of "screenshot this native view").
//
// Rather than rasterizing arbitrary DOM (what a generic html-to-canvas
// library attempts, and often gets subtly wrong), this reconstructs the
// exact crop mathematically from the same values TonyteCropModal already
// tracks in state (zoom, pan offset, frame size) plus the source image's
// natural pixel dimensions — since the preview `<Image resizeMode="cover">`
// is CSS `object-fit: cover` under react-native-web, and the pan/zoom is a
// plain `translate() scale()` transform applied on top of that.
export type WebCropInput = {
  uri: string;
  frame: { w: number; h: number };
  zoom: number;
  offset: { x: number; y: number };
  mode?: 'fit' | 'crop';
};

function loadImage(uri: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for cropping'));
    img.src = uri;
  });
}

// Output resolution: 2x the on-screen frame size (comfortably sharp on a
// retina display) capped at 1600px on the long edge — imagePipeline.web.ts
// re-compresses/resizes whatever URI comes out of this anyway (same as
// every other picked image), so this only needs to be "good enough in",
// not final-quality.
const MAX_OUTPUT_EDGE = 1600;

export async function captureWebCrop({ uri, frame, zoom, offset, mode = 'crop' }: WebCropInput): Promise<string> {
  const img = await loadImage(uri);
  const naturalW = img.naturalWidth;
  const naturalH = img.naturalHeight;

  // object-fit: cover — the browser scales the source image up just enough
  // that it fully covers the frame box on both axes, centered, before any
  // of the modal's own pan/zoom transform is applied on top.
  const baseScale = Math.max(frame.w / naturalW, frame.h / naturalH);
  const totalScale = baseScale * zoom;

  const outScale = Math.min(MAX_OUTPUT_EDGE / Math.max(frame.w, frame.h), 2);
  const outW = Math.max(1, Math.round(frame.w * outScale));
  const outH = Math.max(1, Math.round(frame.h * outScale));

  // Inverse-map the destination frame rect back into source-image pixel
  // space. CSS `translate(x,y) scale(s)` scales first (around the element's
  // own center) then applies the translation as a flat, unscaled pixel
  // offset — matching PanResponder's `gesture.dx/dy` being raw screen
  // pixels regardless of zoom, which is what makes dragging feel consistent
  // at any zoom level in the original modal.
  const sWidth = frame.w / totalScale;
  const sHeight = frame.h / totalScale;
  const sx = naturalW / 2 - offset.x / totalScale - sWidth / 2;
  const sy = naturalH / 2 - offset.y / totalScale - sHeight / 2;

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  if (mode === 'fit') {
    // Fill the frame with a subdued, image-derived backdrop, then draw the
    // complete poster over it. This is the same composition shown by the
    // native/web preview and avoids stretching or trimming poster artwork.
    const coverScale = Math.max(outW / naturalW, outH / naturalH);
    const bgW = naturalW * coverScale;
    const bgH = naturalH * coverScale;
    ctx.save();
    ctx.filter = `blur(${Math.max(12, Math.round(outW * 0.035))}px) brightness(0.42)`;
    ctx.drawImage(img, (outW - bgW) / 2, (outH - bgH) / 2, bgW, bgH);
    ctx.restore();
    ctx.fillStyle = 'rgba(4, 8, 16, 0.28)';
    ctx.fillRect(0, 0, outW, outH);

    const fit = fitImageRect({ width: naturalW, height: naturalH }, { width: outW, height: outH });
    ctx.drawImage(img, fit.x, fit.y, fit.width, fit.height);
  } else {
    ctx.drawImage(img, sx, sy, sWidth, sHeight, 0, 0, outW, outH);
  }

  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
  if (!blob) throw new Error('Canvas crop export failed');
  return URL.createObjectURL(blob);
}
